import * as Sentry from "@sentry/react";
import jwtDecode from "jwt-decode";

import { OpenAPI } from "pwapi/core/OpenAPI";

import { JwtResponse } from "custom-models";

import { PlatformVersion } from "custom-models/JwtResponse";

import {
  getAnyAuthCookie,
  removeAuthCookie,
  getAuthCookie,
  setAuthCookie,
} from "./cookie";
import { Jwt, AuthState } from "../reducers/authReducer";

let packageName = "test";
try {
  packageName = require("/package.json").name;
} catch (e) {}

export const getPreloadedStateFromCookie = () => {
  const preloadedState: { auth?: AuthState } = {};

  const authCookie = getAnyAuthCookie();
  if (authCookie) {
    try {
      // Check authCookie.access.token and authCookie.refresh.token for backwards compatability.
      const accessToken = authCookie.accessToken || authCookie.access?.token;
      const refreshToken = authCookie.refreshToken || authCookie.refresh?.token;
      const longLivedToken = authCookie.longLivedToken;

      const access: Jwt | undefined = accessToken
        ? jwtDecode(accessToken)
        : undefined;
      const refresh: Jwt | undefined = refreshToken
        ? jwtDecode(refreshToken)
        : undefined;

      const expiredRefresh = refresh && 1000 * refresh.exp <= Date.now();

      if (expiredRefresh) {
        removeAuthCookie();
      } else {
        preloadedState.auth = {
          access: access ? jwtDecode(accessToken) : undefined,
          refresh: refresh ? jwtDecode(refreshToken) : undefined,
          longLivedToken,
          errors: {},
          mfa_code_required: false,
          mfa_setup_required: !!authCookie.mfaSetupRequired,
          has_email: !!authCookie.hasEmail,
          has_phone: !!authCookie.hasPhone,
          versions: {},
        };
        OpenAPI.TOKEN = accessToken || longLivedToken;
        OpenAPI.REFRESH_TOKEN = refreshToken;
      }
    } catch (error) {
      console.error(error);
      removeAuthCookie();
    }
  }

  return preloadedState;
};

export function setCookieAndOpenAPI(response: JwtResponse) {
  const newAuthCookie = { ...getAuthCookie() };
  if (response.access) {
    OpenAPI.TOKEN = response.access;
    newAuthCookie.accessToken = response.access;
  }
  if (response.refresh) {
    OpenAPI.REFRESH_TOKEN = response.refresh;
    newAuthCookie.refreshToken = response.refresh;
  }
  if (response.mfa_setup_required !== undefined) {
    newAuthCookie.mfaSetupRequired = response.mfa_setup_required;
  }
  if (response.has_email !== undefined) {
    newAuthCookie.hasEmail = response.has_email;
  }
  if (response.has_phone !== undefined) {
    newAuthCookie.hasPhone = response.has_phone;
  }

  setAuthCookie(newAuthCookie);
}

export function unsetCookieAndOpenAPI() {
  OpenAPI.TOKEN = undefined;
  OpenAPI.REFRESH_TOKEN = undefined;

  removeAuthCookie();
}

// _isVersionOutdated returns undefined if versions match or if versions cannot be compared.
// It will return a string with an explanation message if versions do not match.
const _isVersionOutdated = (
  newestVersion: PlatformVersion,
  currentVersion: string
): undefined | string => {
  if (newestVersion.version === currentVersion) return;

  const newVersions = newestVersion.version
    .split(".")
    .map((s) => parseInt(s))
    .filter((i) => !isNaN(i));
  const curVersions = currentVersion
    ?.split(".")
    .map((s) => parseInt(s))
    .filter((i) => !isNaN(i));

  if (newVersions.length !== 3 || curVersions.length !== 3) {
    const msg = `Either ${newestVersion.version} or ${currentVersion} is an invalid version number`;
    Sentry.captureException(new Error(msg));
    console.warn(msg, newVersions, curVersions);
    return;
  }

  const reportBackwards = (level: "major" | "minor" | "patch") => {
    const msg =
      `Server is reporting an older ${level} version than what is currently loaded:` +
      `\nserver = ${newestVersion.version}` +
      `\nloaded = ${currentVersion}`;
    Sentry.captureException(new Error(msg));
    console.warn(msg);
  };

  const majorDiff = newVersions[0] - curVersions[0];
  let minorDiff = newVersions[1] - curVersions[1];
  let patchDiff = newVersions[2] - curVersions[2];

  if (majorDiff < 0) {
    reportBackwards("major");
    return;
  } else if (majorDiff > 0) {
    minorDiff = newVersions[1];
    patchDiff = newVersions[2];
  } else {
    if (minorDiff < 0) {
      reportBackwards("minor");
      return;
    } else if (minorDiff > 0) {
      patchDiff = newVersions[2];
    } else {
      if (patchDiff < 0) {
        reportBackwards("patch");
        return;
      } else if (patchDiff === 0) {
        throw new Error(
          `${newestVersion.version} doesn't equal ${currentVersion} and yet each individual version matches`
        );
      }
    }
  }

  const msgParts = [];
  if (majorDiff)
    msgParts.push(`${majorDiff} major version${majorDiff === 1 ? "" : "s"}`);
  if (minorDiff)
    msgParts.push(`${minorDiff} minor version${minorDiff === 1 ? "" : "s"}`);
  if (patchDiff)
    msgParts.push(`${patchDiff} patch version${patchDiff === 1 ? "" : "s"}`);

  if (msgParts.length === 1) return msgParts[0];
  if (msgParts.length === 2) return msgParts.join(" and ");
  return `${msgParts.slice(0, -1).join(", ")}, and ${
    msgParts[msgParts.length - 1]
  }`;
};

// isVersionOutdated returns undefined if versions match or if versions cannot be compared.
// It will return a string with an explanation message if versions do not match.
export const isVersionOutdated = (
  newVersions: JwtResponse["versions"]
): undefined | string => {
  if (
    packageName === "lis" &&
    newVersions?.lis_frontend &&
    process.env.REACT_APP_LIS_FRONTEND_VERSION
  ) {
    return _isVersionOutdated(
      newVersions.lis_frontend,
      process.env.REACT_APP_LIS_FRONTEND_VERSION
    );
  }

  if (
    packageName === "viewer" &&
    newVersions?.viewer_frontend &&
    process.env.REACT_APP_VIEWER_FRONTEND_VERSION
  ) {
    return _isVersionOutdated(
      newVersions.viewer_frontend,
      process.env.REACT_APP_VIEWER_FRONTEND_VERSION
    );
  }

  return;
};
